import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomerStatusModalBase } from '../customer-status-modal.base';

/**
 * Modal component that presents an "inactive" customer status message and allows the user to close or confirm an action.
 * @component
 * @selector app-customer-status-inactive-modal
 * @example
 *   <app-customer-status-inactive-modal [isVisible]="true" [isLoading]="false" (close)="onClose()" (confirm)="onConfirm()"></app-customer-status-inactive-modal>
 * @input boolean isVisible - Controls whether the modal is visible; when true the modal is shown.
 * @input boolean isLoading - Indicates a loading state (e.g., while a confirmation action is in progress) and can be used to disable controls or show a spinner.
 * @output EventEmitter<void> close - Emitted when the modal should be closed (for example, when the user cancels, clicks a close button, or clicks the backdrop).
 * @output EventEmitter<void> confirm - Emitted when the user confirms the presented action (for example, acknowledging the inactive status or confirming a reactivation).
 *
 * Notes:
 * - This component extends CustomerStatusModalBase and relies on shared behavior and presentation logic provided by that base class.
 * - Key interactions are handled by emitting the close and confirm events; callers should subscribe to these outputs to perform navigation, state updates, or API calls.
 * - The component itself does not declare additional public methods or lifecycle hooks; any setup/teardown behavior should be handled in the base class or by the parent component responding to the outputs.
 */
@Component({
  selector: 'app-customer-status-inactive-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customer-status-inactive-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-status-inactive-modal.component.scss'],
})
export class CustomerStatusInactiveModalComponent extends CustomerStatusModalBase {
  @Input() isVisible = false;
  @Input() isLoading = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();
}
